import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useFeedback = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const createFeedback = useCallback(
    async ({ rating, viewKey, bookingNumber, text }) => {
      try {
        return await sendRequest({
          url: '/api/v1/feedback',
          method: 'POST',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { rating, viewKey, bookingNumber, text },
        });
      } catch (err) {
        toast.error('Fehler beim Senden des Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getMyFeedback = useCallback(
    async ({ includeSolved = true, markAsRead = false } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/feedback/me',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { includeSolved, markAsRead },
        });
      } catch (err) {
        toast.error('Fehler beim Laden deines Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateMyFeedback = useCallback(
    async ({ feedbackId, rating, viewKey, bookingNumber, text }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/me/${feedbackId}`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { rating, viewKey, bookingNumber, text },
        });
      } catch (err) {
        toast.error('Fehler beim Bearbeiten deines Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const deleteMyFeedback = useCallback(
    async feedbackId => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/me/${feedbackId}`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Loeschen deines Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const replyToMyFeedback = useCallback(
    async ({ feedbackId, text }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/me/${feedbackId}/reply`,
          method: 'POST',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { text },
        });
      } catch (err) {
        toast.error('Fehler beim Senden der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getAllFeedback = useCallback(
    async ({ includeSolved = true } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/feedback',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { includeSolved },
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Single feedback entry, used by the developer to-do board to show the
  // feedback a task was derived from without leaving the board. Holders of the
  // developer to-do right may read exactly the entries a to-do links to.
  const getFeedbackById = useCallback(
    async feedbackId => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden des Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const setSolved = useCallback(
    async ({ feedbackId, solved, response }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/solved`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { solved, response },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Feedback-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const setResponse = useCallback(
    async ({ feedbackId, response }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/response`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { response },
        });
      } catch (err) {
        toast.error('Fehler beim Speichern der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const addResponseMessage = useCallback(
    async ({ feedbackId, text }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/reply`,
          method: 'POST',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { text },
        });
      } catch (err) {
        toast.error('Fehler beim Senden der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // `response` is the reason the author is told about; the backend stores it as
  // the answer when rejecting an entry that has none yet.
  const setRejected = useCallback(
    async ({ feedbackId, rejected, response }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/rejected`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { rejected, response },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Feedback-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Sets the severity (p1-p5) of an issue. Pass null to clear it again.
  const setPriority = useCallback(
    async ({ feedbackId, priority, note }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/priority`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { priority: priority || null, note },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren der Priorität');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Forwards a feedback entry to the IT ticket system: creates an issue with
  // the chosen priority (p1-p5) and closes the feedback with a reply to the
  // author in one step. Returns { feedback, issueId }.
  const forwardToIssue = useCallback(
    async ({ feedbackId, priority, branchId, title, response }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/forwardToIssue`,
          method: 'POST',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { priority, branchId, title, response },
        });
      } catch (err) {
        toast.error('Fehler beim Weiterleiten an die IT');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const setInProgress = useCallback(
    async ({ feedbackId, inProgress }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/${feedbackId}/inProgress`,
          method: 'PATCH',
          headers: {
            ...authCtx.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: { inProgress },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Feedback-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Attaches one prepared screenshot to an existing feedback entry.
  const uploadFeedbackScreenshot = useCallback(
    async ({ feedbackId, fileName, contentType, dataBase64 }) => {
      return await sendRequest({
        url: `/api/v1/feedback/${feedbackId}/screenshot`,
        method: 'POST',
        headers: {
          ...authCtx.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: { fileName, contentType, dataBase64 },
      });
    },
    [authCtx, sendRequest]
  );

  // Uploads a list of prepared screenshots and returns how many failed. The
  // feedback itself is already saved at this point, so a failed image must not
  // look like a failed submission.
  const uploadFeedbackScreenshots = useCallback(
    async (feedbackId, screenshots) => {
      let failed = 0;
      for (const screenshot of screenshots) {
        try {
          await uploadFeedbackScreenshot({ feedbackId, ...screenshot });
        } catch (err) {
          failed += 1;
        }
      }
      return failed;
    },
    [uploadFeedbackScreenshot]
  );

  const deleteFeedbackScreenshot = useCallback(
    async ({ feedbackId, screenshotId }) => {
      try {
        return await sendRequest({
          url: `/api/v1/feedback/me/${feedbackId}/screenshot/${screenshotId}`,
          method: 'DELETE',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Entfernen des Screenshots');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // The screenshot endpoint needs the auth header, so an <img src> cannot load
  // it directly; callers turn this blob into an object URL.
  const getFeedbackScreenshotBlob = useCallback(
    async ({ feedbackId, screenshotId }) => {
      return await sendRequest({
        url: `/api/v1/feedback/${feedbackId}/screenshot/${screenshotId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
        returnBlob: true,
      });
    },
    [authCtx, sendRequest]
  );

  return {
    createFeedback,
    getMyFeedback,
    updateMyFeedback,
    deleteMyFeedback,
    replyToMyFeedback,
    getAllFeedback,
    getFeedbackById,
    setSolved,
    setResponse,
    addResponseMessage,
    setRejected,
    setInProgress,
    setPriority,
    forwardToIssue,
    uploadFeedbackScreenshot,
    uploadFeedbackScreenshots,
    deleteFeedbackScreenshot,
    getFeedbackScreenshotBlob,
  };
};

export default useFeedback;

